import React, { useState } from 'react';
import ExcelJS from 'exceljs';
import SimCardDownloadIcon from '@mui/icons-material/SimCardDownload';
import DateRangeIcon from '@mui/icons-material/DateRange';
import {
  Box, Button, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Typography, Stack, Tooltip, IconButton
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

/* ===== Helpers ===== */
const toExcelDate = (v) => {
  if (v instanceof Date && !isNaN(v.getTime())) return v;
  if (typeof v === 'string') {
    const s = v.trim();
    if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
      const d = new Date(s);
      if (!isNaN(d.getTime())) return d;
    }
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) {
      const [dd, mm, yyyy] = s.split('/').map(Number);
      const d = new Date(yyyy, mm - 1, dd);
      if (!isNaN(d.getTime())) return d;
    }
  }
  return v ?? '';
};

const isNumberLike = (v) => {
  if (v == null || v === '') return false;
  return !isNaN(Number(String(v).replace(/[^\d.-]/g, '')));
};

const toNumber = (v) => Number(String(v).replace(/[^\d.-]/g, ''));

const cleanExcelText = (value) => String(value ?? '')
  .replace(/\u2022/g, '-')
  .replace(/\u2019/g, "'")
  .replace(/\u00e2/g, '-');

const normalizePartidasText = (value) => cleanExcelText(value)
  .split('\n')
  .map(line => line.replace(/^\s*[^A-Za-z0-9]+/, '- '))
  .join('\n');

const getExcelColLetter = (n) => {
  let name = '';
  while (n > 0) {
    const m = (n - 1) % 26;
    name = String.fromCharCode(65 + m) + name;
    n = Math.floor((n - m) / 26);
  }
  return name;
};

// Campos que deben formatearse como moneda
const MONEY_KEYS = ['TOTAL', 'IMPORTE', 'MONTOSOLICITADO', 'MONTO_SOLICITADO', 'TOTALOC', 'TOTAL_OC', 'TOTAL_PART', 'COSTOU', 'COSTO_UNITARIO', 'PENDIENTE', 'PROGRAMAR A PAGO'];
// Campos que deben formatearse como fecha
const DATE_KEYS = [
  'FECHA', 'FECHASOLICITUD', 'FECHA_OC', 'FECHA_SOLICITUD', 'FECHA_PAGO', 'FECHA_FACTURA', 'FECHACREACION',
  'FECHA_AUTORIZACION', 'FECHA_VENCIMIENTO', 'FECHA_SOLICITUD_PAGO', 'ULTIMA_SOLICITUD',
  'FECHA_AUT_N1', 'FECHA_AUT_N2', 'FECHA_AUT_N3', 'FECHA_TESO_N1',
  'EXPORT_SOLICITUD_FECHA', 'EXPORT_SOLICITAR_PAGO_FECHA', 'EXPORT_PAGADO_FECHA',
  'EXPORT_ENTRADA_FECHA', 'EXPORT_COMPROBANTE_FECHA', 'REPORTE_SOLICITADO_FECHA',
  'ENTRADA_FECHA', 'PAGO_ERP_FECHA', 'COMPROBANTE_FECHA', 'FECHA_COMPROBANTE'
];

const ADMIN_TRACKING_EXPORT_KEYS = new Set([
  'EXPORT_SOLICITUD',
  'EXPORT_SOLICITUD_FECHA',
  'EXPORT_SOLICITAR_PAGO',
  'EXPORT_SOLICITAR_PAGO_FECHA',
  'EXPORT_PAGADO',
  'EXPORT_PAGADO_FECHA',
  'EXPORT_ENTRADA',
  'EXPORT_ENTRADA_FECHA',
  'EXPORT_COMPROBANTE',
  'EXPORT_COMPROBANTE_FECHA',
]);

const ADMIN_AGING_EXPORT_KEYS = new Set([
  'EXPORT_DIAS_SIN_PAGO',
  'EXPORT_MESES_SIN_PAGO',
]);

const SOLICITUDES_COL_MAP = [
  { key: 'EXHIBIT',              label: 'EXHIBIT' },
  { key: 'FOLIO',                label: 'ORDEN COMPRA' },
  { key: 'REQUI',                label: 'REQUISICION' },
  { key: 'EMPRESA',              label: 'EMPRESA' },
  { key: 'FECHA_OC',             label: 'FECHA OC' },
  { key: 'FECHA_SOLICITUD',      label: 'FECHA SOLICITUD' },
  { key: 'REQUISITOR',           label: 'REQUISITOR' },
  { key: 'PROVEEDOR',            label: 'PROVEEDOR' },
  { key: 'SOLICITANTE',          label: 'COMPRADOR' },
  { key: 'NUMEROCC',             label: 'NUMERO CENTRO COSTO' },
  { key: 'CENTROCOSTO',          label: 'CENTRO COSTO' },
  { key: 'DEPARTAMENTO',         label: 'DEPARTAMENTO' },
  { key: 'TOTAL',                label: 'TOTAL OC' },
  { key: 'DIVISA',               label: 'DIVISA' },
  { key: 'DESCRIPCION',          label: 'DESCRIPCION' },
  { key: 'MONTOSOLICITADO',      label: 'SOLICITADO' },
  { key: 'PENDIENTE',            label: 'RESTANTE' },
  { key: 'NOTAS_OBSERVACIONES', label: 'NOTAS OBSERVACIONES' },
  { key: 'FOLIO_FACTURA',        label: 'FACTURAS' },
  { key: 'NUMERO_ECONOMICO',     label: 'NUMERO ECONOMICO' },
  { key: 'ESTATUS',              label: 'ESTATUS' },
  { key: 'FECHA_PAGO',            label: 'FECHA DE PAGO' },
];

const ADMIN_SOLICITUDES_COL_MAP = [
  { key: 'ID', label: 'ID' },
  { key: 'EXHIBIT', label: 'EXHIBIT' },
  { key: 'REQUI', label: 'REQUI' },
  { key: 'FOLIO', label: 'ORDEN' },
  { key: 'FECHA_OC', label: 'FECHA OC' },
  { key: 'EMPRESA', label: 'EMPRESA' },
  { key: 'SOLICITANTE', label: 'COMPRADOR' },
  { key: 'REQUISITOR', label: 'REQUISITOR' },
  { key: 'PROVEEDOR', label: 'PROVEEDOR' },
  { key: 'NUMEROCC', label: 'NUMERO CENTRO COSTO' },
  { key: 'CENTROCOSTO', label: 'NOMBRE CC' },
  { key: 'DEPARTAMENTO', label: 'DEPARTAMENTO' },
  { key: 'NUMERO_ECONOMICO', label: 'ECONOMICO' },
  { key: 'TOTAL', label: 'TOTAL OC' },
  { key: 'DIVISA', label: 'DIVISA' },
  { key: 'DESCRIPCION', label: 'DESCRIPCION' },
  { key: 'MONTOSOLICITADO', label: 'PROGRAMAR A PAGO' },
  { key: 'PENDIENTE', label: 'PENDIENTE' },
  { key: 'ESTATUS', label: 'ESTATUS' },
  { key: 'EXPORT_SOLICITUD', label: 'SOLICITUD' },
  { key: 'EXPORT_SOLICITUD_FECHA', label: 'FECHA SOLICITUD' },
  { key: 'EXPORT_SOLICITAR_PAGO', label: 'SOLICITAR PAGO' },
  { key: 'EXPORT_SOLICITAR_PAGO_FECHA', label: 'FECHA SOLICITAR PAGO' },
  { key: 'EXPORT_PAGADO', label: 'PAGADO' },
  { key: 'EXPORT_PAGADO_FECHA', label: 'FECHA PAGO' },
  { key: 'EXPORT_DIAS_SIN_PAGO', label: 'DIAS SIN PAGO' },
  { key: 'EXPORT_MESES_SIN_PAGO', label: 'MESES SIN PAGO' },
  { key: 'EXPORT_ENTRADA', label: 'ENTRADA' },
  { key: 'EXPORT_ENTRADA_FECHA', label: 'FECHA ENTRADA' },
  { key: 'EXPORT_COMPROBANTE', label: 'COMPROBANTE' },
  { key: 'EXPORT_COMPROBANTE_FECHA', label: 'FECHA COMPROBANTE' },
];

const ExportButton = ({
  data,
  onFetchAll = null,
  fileName = 'Exportacion.xlsx',
  columnMap = null,
  hiddenFields = [],
  columnLabels = {},
  flattenPartidas = false,
  summaryMode = false,
  hidePendientes = false,
  customExportOptions = null,
  tableLoading = false,
  disabled = false,
  btnSx = {},
}) => {
  const [loading, setLoading] = React.useState(false);
  const isButtonDisabled = Boolean(loading || tableLoading || disabled);
  const [dateDialog, setDateDialog] = useState({ open: false, variant: null });
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [exportScope, setExportScope] = useState('filters');

  const hasPartidasData = React.useMemo(() => {
    const rows = Array.isArray(data?.contenido) ? data.contenido : (Array.isArray(data) ? data : []);
    return rows.some(r => Array.isArray(r.partidas) && r.partidas.length > 0);
  }, [data]);

  const useLegacyMode = !columnMap && hasPartidasData;

  const legacyExportOptions = React.useMemo(() => {
    if (Array.isArray(customExportOptions) && customExportOptions.length > 0) {
      return customExportOptions;
    }
    return [
      {
        key: 'summary',
        label: 'Reporte con descripcion',
        description: 'Incluye la informacion principal de la OC y la descripcion de partidas resumida.',
      },
      ...(!hidePendientes ? [{
        key: 'pendientes',
        label: 'Pendientes de pago',
        description: 'Enfocado en importes por solicitar, saldo pendiente, estatus y datos de pago.',
      }] : []),
    ];
  }, [customExportOptions, hidePendientes]);

  const openDateDialog = (variant) => {
    setDateFrom('');
    setDateTo('');
    setExportScope('filters');
    const defaultVariant = variant || (Array.isArray(customExportOptions) && customExportOptions[0]?.key) || legacyExportOptions[0]?.key || 'summary';
    setDateDialog({ open: true, variant: defaultVariant });
  };

  const closeDateDialog = () => {
    setDateDialog({ open: false, variant: null });
    setDateFrom('');
    setDateTo('');
    setExportScope('filters');
  };

  const selectedExportOption = React.useMemo(
    () => legacyExportOptions.find(option => option.key === dateDialog.variant) || legacyExportOptions[0],
    [dateDialog.variant, legacyExportOptions]
  );

  const exportScopeOptions = React.useMemo(() => ([
    {
      key: 'filters',
      label: 'Filtros actuales',
      description: 'Descarga todo lo que coincide con los filtros aplicados en la tabla.',
    },
    {
      key: 'dateRange',
      label: 'Solo rango de fechas',
      description: 'Ignora filtros de tabla y usa solamente Fecha de Solicitud.',
    },
    {
      key: 'all',
      label: 'Todo sin filtros',
      description: 'Descarga todos los registros disponibles para esta pantalla.',
    },
  ]), []);

  const handleExportWithDates = () => {
    const selectedVariant = dateDialog.variant || (Array.isArray(customExportOptions) && customExportOptions[0]?.key) || legacyExportOptions[0]?.key || 'desglosado';
    const effectiveFromDate = exportScope === 'all' ? '' : dateFrom;
    const effectiveToDate = exportScope === 'all' ? '' : dateTo;
    closeDateDialog();
    handleExport(selectedVariant, effectiveFromDate, effectiveToDate, false, exportScope);
  };

  const resolveColumnMap = (exportRows, variant) => {
    if (variant === 'summary' || variant === 'pendientes') return null;
    if (columnMap && columnMap.length > 0) return columnMap;
    const first = exportRows[0] || {};
    const rowKeys = Object.keys(first);
    if (!rowKeys.length) return null;

    const orderedKeys = [];
    const seen = new Set();

    Object.keys(columnLabels || {}).forEach(k => {
      if (rowKeys.includes(k) && !hiddenFields.includes(k) && k !== 'partidas' && !k.endsWith('_COLOR')) {
        orderedKeys.push(k);
        seen.add(k);
      }
    });

    rowKeys.forEach(k => {
      if (!seen.has(k) && !hiddenFields.includes(k) && k !== 'partidas' && !k.endsWith('_COLOR')) {
        orderedKeys.push(k);
        seen.add(k);
      }
    });

    return orderedKeys.map(k => ({ key: k, label: (columnLabels[k] || k).toUpperCase() }));
  };

  const handleExport = async (variant = 'generic', fromDate = '', toDate = '', isTop100 = false, scope = 'filters') => {
    const effectiveVariant = variant || (Array.isArray(customExportOptions) && customExportOptions[0]?.key) || legacyExportOptions[0]?.key || 'generic';
    let exportData = data;
    const fetchedServerSide = Boolean(onFetchAll && typeof onFetchAll === 'function');

    if (fetchedServerSide) {
      try {
        setLoading(true);
        exportData = await onFetchAll({ scope, fromDate, toDate, variant: effectiveVariant });
      } catch (e) {
        console.error('Error fetching all data for export:', e);
        exportData = data;
      } finally {
        setLoading(false);
      }
    }

    let exportRows = Array.isArray(exportData?.contenido) ? exportData.contenido
      : Array.isArray(exportData) ? exportData : [];

    if (isTop100) {
      exportRows = exportRows.slice(0, 100);
    }

    const parseRowDate = (raw) => {
      if (!raw) return null;
      const s = String(raw).trim();
      let d = new Date(s);
      if (!isNaN(d.getTime())) return d;
      const commaFmt = s.replace(/,\s*/, ' ');
      const normalized = commaFmt.replace(/\b24:/, '00:');
      d = new Date(normalized.replace(' ', 'T'));
      if (!isNaN(d.getTime())) return d;
      const dmyMatch = s.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
      if (dmyMatch) {
        d = new Date(`${dmyMatch[3]}-${dmyMatch[2]}-${dmyMatch[1]}`);
        if (!isNaN(d.getTime())) return d;
      }
      return null;
    };

    if (!fetchedServerSide && (fromDate || toDate)) {
      const from = fromDate ? new Date(fromDate + 'T00:00:00') : null;
      const to   = toDate   ? new Date(toDate   + 'T23:59:59') : null;
      exportRows = exportRows.filter(r => {
        const raw = r?.FECHASOLICITUD || r?.FECHA || '';
        if (!raw) return false;
        const d = parseRowDate(raw);
        if (!d) return false;
        if (from && d < from) return false;
        if (to   && d > to  ) return false;
        return true;
      });
    }

    if (!exportRows.length) return;

    const isSummary = variant === 'summary';
    const isDetailed = variant === 'detailed';
    const isPendientes = variant === 'pendientes';
    const isDesglosado = variant === 'desglosado';
    const base = (fileName || 'Exportacion').replace(/\.xlsx$/i, '');
    let currentFileName = `${base}.xlsx`;
    if (isSummary) currentFileName = `${base}_Resumen.xlsx`;
    if (isDetailed) currentFileName = `${base}_Detallado.xlsx`;
    if (isPendientes) currentFileName = `${base}_Pendientes.xlsx`;
    if (isDesglosado) currentFileName = `${base}_Desglosado_Solicitudes.xlsx`;

    const resolvedMap = resolveColumnMap(exportRows, variant);

    if (resolvedMap && !isPendientes) {
      await exportGeneric(exportRows, resolvedMap, currentFileName);
    } else {
      await exportLegacy(exportRows, currentFileName, isSummary, isDetailed, isPendientes, columnLabels, hiddenFields);
    }
  };

  const exportGeneric = async (rows, colMap, outFileName) => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Datos');

    const headerRow = worksheet.addRow(colMap.map(c => c.label));
    headerRow.height = 26;
    headerRow.eachCell((cell) => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '0655A5' } };
      cell.font = { name: 'Arial', size: 10, color: { argb: 'FFFFFF' }, bold: true };
      cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
      cell.border = {
        top: { style: 'thin', color: { argb: 'FFFFFF' } },
        bottom: { style: 'medium', color: { argb: '000000' } },
        left: { style: 'thin', color: { argb: 'FFFFFF' } },
        right: { style: 'thin', color: { argb: 'FFFFFF' } },
      };
    });

    rows.forEach((r, idx) => {
      const values = colMap.map(({ key }) => {
        if ((key === 'DESCRIPCION' || key === 'DETALLES_RESUMEN') && (!r?.[key] || Array.isArray(r?.partidas))) {
          const rowPartidas = Array.isArray(r?.partidas) ? r.partidas : [];
          if (rowPartidas.length > 0) {
            const descLista = rowPartidas
              .map(p => `- ${p.DESCRIPCION_PART || p.DESCRIPCION || p.CONCEPTO_PROYECTO || 'S/D'}${p.CANTIDAD ? ` (Ct: ${p.CANTIDAD})` : ''}`)
              .join('\n');
            return normalizePartidasText(descLista);
          }
          return r?.DESCRIPCION || r?.DESCRIPCION_PART || r?.CONCEPTO_PROYECTO || r?.NOTAS || '';
        }
        const raw = r?.[key];
        if (Array.isArray(raw)) return raw.join(', ');
        if (key === 'Activo' || key === 'ACTIVO') {
          if (raw === 1 || raw === true || raw === '1') return 'Activo';
          if (raw === 0 || raw === false || raw === '0') return 'Inactivo';
        }
        return raw ?? '';
      });

      const row = worksheet.addRow(values);
      const bg = idx % 2 === 0 ? 'FFFFFF' : 'F8FAFC';

      // Altura dinámica: cuenta líneas reales (\n) + wrap visual de líneas largas
      const MULTILINE_COLS_WIDTHS = { DESCRIPCION: 60, NOTAS_REQUI: 45 };
      let totalVisualLines = 1;
      colMap.forEach(({ key }) => {
        if (MULTILINE_COLS_WIDTHS[key] !== undefined) {
          const val = String(r?.[key] ?? '');
          if (!val) return;
          const colChars = MULTILINE_COLS_WIDTHS[key];
          // Por cada línea real, calcula cuántas filas visuales ocupa
          const visualLines = val.split('\n').reduce((sum, line) => {
            return sum + Math.max(1, Math.ceil(line.length / colChars));
          }, 0);
          if (visualLines > totalVisualLines) totalVisualLines = visualLines;
        }
      });
      row.height = totalVisualLines > 1 ? Math.min(Math.max(20, totalVisualLines * 15), 200) : 18;

      row.eachCell((cell, colNum) => {
        const key = colMap[colNum - 1]?.key || '';
        const raw = cell.value;
        const colorHint = r?.[`${key}_COLOR`]; // companion _COLOR field
        cell.font = { name: 'Arial', size: 9, color: { argb: '1E293B' } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bg } };
        cell.alignment = { vertical: 'middle', horizontal: 'left' };
        cell.border = { bottom: { style: 'thin', color: { argb: 'E2E8F0' } } };

        // DESCRIPCION: multilinea con wrapText igual que Solicitudes
        if (key === 'DESCRIPCION') {
          cell.alignment = { vertical: 'top', horizontal: 'left', wrapText: true };
        }

        if (DATE_KEYS.some(dk => key.toUpperCase().includes(dk))) {
          const d = toExcelDate(raw);
          if (d instanceof Date && !isNaN(d.getTime())) {
            cell.value = d;
            cell.numFmt = 'dd/mm/yyyy';
            cell.alignment = { vertical: 'middle', horizontal: 'center' };
          }
        }

        if (MONEY_KEYS.some(mk => key.toUpperCase().includes(mk)) && isNumberLike(raw)) {
          cell.value = toNumber(raw);
          cell.numFmt = '$#,##0.00';
          cell.alignment = { vertical: 'middle', horizontal: 'right' };
          cell.font = { name: 'Arial', size: 9, bold: true };
        }

        // Color por companion _COLOR o por ESTATUS/ACTIVO
        if (colorHint === 'GREEN') {
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'DCFCE7' } };
          cell.font = { name: 'Arial', size: 9, bold: true, color: { argb: '14532D' } };
          cell.alignment = { vertical: 'middle', horizontal: 'center' };
        } else if (colorHint === 'RED') {
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FEE2E2' } };
          cell.font = { name: 'Arial', size: 9, bold: true, color: { argb: '7F1D1D' } };
          cell.alignment = { vertical: 'middle', horizontal: 'center' };
        } else if (key === 'DIAS_SIN_PAGO' || key === 'MESES_SIN_PAGO') {
          if (raw !== '' && raw !== null && raw !== undefined) {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FEF9C3' } };
            cell.font = { name: 'Arial', size: 9, bold: true, color: { argb: '713F12' } };
            cell.alignment = { vertical: 'middle', horizontal: 'center' };
          }
        } else if (key.toUpperCase() === 'ESTATUS' || key.toUpperCase() === 'ACTIVO') {
          const v = String(raw ?? '').toLowerCase();
          let fillColor = bg;
          let fontColor = '1E293B';
          if (v === 'no' || v.includes('cancel') || v.includes('inactiv') || v.includes('rechaz')) {
            fillColor = 'FEE2E2';
            fontColor = '991B1B';
          } else if (v.includes('autoriz')) {
            fillColor = 'DBEAFE';
            fontColor = '1E40AF';
          } else if (v === 'si' || v.includes('activ') || v.includes('pagad')) {
            fillColor = 'DCFCE7';
            fontColor = '166534';
          } else if (v.includes('proceso') || v.includes('pendient')) {
            fillColor = 'FEF9C3';
            fontColor = '854D0E';
          }
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: fillColor } };
          cell.alignment = { vertical: 'middle', horizontal: 'center' };
          cell.font = { name: 'Arial', size: 9, bold: true, color: { argb: fontColor } };
        }
      });
    });

    colMap.forEach(({ key, label }, i) => {
      // Columnas de texto libre: ancho fijo grande
      if (key === 'DESCRIPCION') { worksheet.getColumn(i + 1).width = 60; return; }
      if (key === 'NOTAS_REQUI') { worksheet.getColumn(i + 1).width = 45; return; }
      let max = label.length;
      rows.forEach(r => {
        const v = r?.[key];
        const s = Array.isArray(v) ? v.join(', ') : String(v ?? '');
        if (s.length > max) max = s.length;
      });
      worksheet.getColumn(i + 1).width = Math.min(Math.max(max + 3, 12), 50);
    });

    worksheet.views = [{ state: 'frozen', ySplit: 1 }];
    const lastLetter = getExcelColLetter(colMap.length);
    worksheet.autoFilter = `A1:${lastLetter}1`;

    await downloadWorkbook(workbook, outFileName);
  };

  const exportLegacy = async (rows, outFileName, isSummary, isDetailed, isPendientes, colLabels, hidFields) => {
    let finalColMap = [];

    if (isPendientes) {
      rows = rows.filter(r => {
        const st = String(r?.ESTATUS || '').toLowerCase().trim();
        // Excluir si ya fue Pagada o Cancelada/Rechazada
        if (st.includes('pagad') || st.includes('cancelad') || st.includes('rechazad')) return false;
        // Incluir Autorizada, Proceso, Default, Vencida o cualquier estado pendiente
        return true;
      });
      finalColMap = [
        { key: 'FOLIO',          label: 'ORDEN' },
        { key: 'MONTOSOLICITADO',label: 'PAGO SOLICITADO' },
        { key: 'DIVISA',         label: 'MONEDA' },
        { key: 'FECHA_SOLICITUD',label: 'FECHA SOLICITUD' },
        { key: 'DETALLES_RESUMEN', label: 'PARTIDAS' },
        { key: 'NUMERO_ECONOMICO',label: 'ECONOMICO' },
        { key: 'CENTROCOSTO',    label: 'CENTRO COSTOS' },
        { key: 'PROVEEDOR',      label: 'PROVEEDOR' },
        { key: 'ESTATUS',        label: 'ESTATUS' },
        { key: 'AUTORIZAR',      label: 'AUTORIZAR' },
        { key: 'CONFIRMACION',   label: 'CONFIRMACION' }
      ];
    } else {
      const hasAdminTrackingExport = rows.some(r => r?.EXPORT_SOLICITUD !== undefined);
      const baseColumnMap = hasAdminTrackingExport ? ADMIN_SOLICITUDES_COL_MAP : SOLICITUDES_COL_MAP;
      finalColMap = baseColumnMap.filter(({ key }) => {
        if (hasAdminTrackingExport && (key === 'TIENE_SOLICITUD_TXT' || key === 'FECHA_SOLICITUD_PAGO')) return false;
        if (hidFields.includes(key)) return false;
        if (key === 'TIENE_SOLICITUD_TXT') return true;
        if (key === 'DESCRIPCION') return rows.some(r => r?.DESCRIPCION !== undefined || r?.DESCRIPCION_PART !== undefined || (Array.isArray(r?.partidas) && r.partidas.length > 0));
        if (ADMIN_TRACKING_EXPORT_KEYS.has(key)) return hasAdminTrackingExport;
        if (ADMIN_AGING_EXPORT_KEYS.has(key)) return hasAdminTrackingExport;
        return rows.some(r => r[key] !== undefined);
      });
    }

    let partidaKeys = [];
    const rowsWithPartidas = rows.filter(r => Array.isArray(r.partidas) && r.partidas.length > 0);
    if (rowsWithPartidas.length > 0) {
      const firstP = rowsWithPartidas[0].partidas[0];
      const explicit = Object.keys(colLabels).filter(k => k in firstP);
      partidaKeys = explicit.length > 0 ? explicit : Object.keys(firstP).filter(k => !hidFields.includes(k));
    }

    const finalColumns = finalColMap.map(c => c.key);
    const finalLabels = finalColMap.map(c => (
      c.key === 'DETALLES_RESUMEN' ? 'DESCRIPCION' : c.label.toUpperCase()
    ));

    if (isPendientes) {
      try {
        const XlsxPopulate = require('xlsx-populate/browser/xlsx-populate');
        let response = await fetch('/PlantillaPendientes.xlsm');
        let isMacro = true;
        
        if (!response.ok) {
          response = await fetch('/PlantillaPendientes.xlsx');
          isMacro = false;
        }

        if (response.ok) {
          const buffer = await response.arrayBuffer();
          const workbookPop = await XlsxPopulate.fromDataAsync(buffer);
          const sheet = workbookPop.sheet(0);
          
          rows.forEach((r, idx) => {
            const rowPartidas = Array.isArray(r.partidas) ? r.partidas : [];
            const descLista = rowPartidas
              .map(p => `- ${p.DESCRIPCION_PART || p.DESCRIPCION || p.CONCEPTO_PROYECTO || 'S/D'}${p.CANTIDAD ? ` (Ct: ${p.CANTIDAD})` : ''}`)
              .join('\n');

            const vals = finalColumns.map(k => {
              if (k === 'DETALLES_RESUMEN' || k === 'DESCRIPCION') {
                if (rowPartidas.length > 0) return normalizePartidasText(descLista);
                return r?.DESCRIPCION || r?.DESCRIPCION_PART || r?.CONCEPTO_PROYECTO || r?.NOTAS || '';
              }
              if (k === 'PENDIENTE') return r?.PENDIENTE ?? r?.RESTANTE ?? '';
              if (k === 'ESTATUS') return mapEstatus(r?.[k]) ?? r?.[k] ?? '';
              return r?.[k] ?? '';
            });

            const rowNum = idx + 2;
            for (let i = 0; i < Math.min(10, finalColumns.length); i++) {
              const key = finalColumns[i];
              const raw = vals[i];
              const cell = sheet.cell(rowNum, i + 1);
              if (DATE_KEYS.includes(key)) {
                const d = toExcelDate(raw);
                if (d instanceof Date && !isNaN(d.getTime())) cell.value(d);
                else cell.value(raw);
              } else if ((MONEY_KEYS.includes(key) || key?.includes('TOTAL')) && isNumberLike(raw)) {
                cell.value(toNumber(raw));
              } else {
                cell.value(raw);
              }
              if (key === 'AUTORIZAR' || key === 'CONFIRMACION') {
                cell.value('☐');
                try {
                  cell.dataValidation({
                    type: 'list',
                    allowBlank: true,
                    formula1: '"☑,☐"'
                  });
                } catch (e) {}
              }
              if (key === 'DETALLES_RESUMEN') {
                cell.style("wrapText", true);
                cell.style("verticalAlignment", "top");
                cell.style("horizontalAlignment", "left");
              }
            }
          });

          const outBuffer = await workbookPop.outputAsync();
          const mimeType = isMacro 
            ? 'application/vnd.ms-excel.sheet.macroEnabled.12'
            : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
          const blob = new Blob([outBuffer], { type: mimeType });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          if (isMacro) {
            outFileName = outFileName.replace(/\.xlsx$/i, '.xlsm');
          }
          a.download = outFileName;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
          return;
        }
      } catch (err) {
        console.warn('Fallo xlsx-populate o no se encontro plantilla, usando fallback:', err);
      }
    }

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Datos');

    // Encabezado
    const headerRow = worksheet.addRow(finalLabels);
    headerRow.height = 24;
    headerRow.eachCell((cell, colNumber) => {
      const key = finalColumns[colNumber - 1];
      const bgColor = key === 'MONTOSOLICITADO' ? 'BBDEFB' : '0655A5';
      const textColor = key === 'MONTOSOLICITADO' ? '000000' : 'FFFFFF';
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bgColor } };
      cell.font = { name: 'Arial', size: 10, color: { argb: textColor }, bold: true };
      cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
      cell.border = {
        top: { style: 'thin', color: { argb: 'FFFFFF' } },
        bottom: { style: 'medium', color: { argb: '000000' } },
        left: { style: 'thin', color: { argb: 'FFFFFF' } },
        right: { style: 'thin', color: { argb: 'FFFFFF' } },
      };
    });

    const mapEstatus = (val) => {
      const n = Number(val);
      if (n === 1) return 'Por Autorizar';
      if (n === 2) return 'En Proceso';
      if (n === 3) return 'Autorizada';
      if (n === 4) return 'Pagada';
      if (n === 5) return 'Declinada';
      if (n === 10) return 'Diferencia Pagada';
      if (n === 12) return 'Rechazada';
      return val;
    };

    const formatTieneSolicitud = (row) => {
      const count = Number(row?.SOLICITUDES_COUNT || 0);
      const rawValue = String(row?.TIENE_SOLICITUD ?? '').trim().toUpperCase();
      if (count > 0) return `${count} solicitud${count === 1 ? '' : 'es'}`;
      if (rawValue === 'SI' || rawValue === 'SÍ' || rawValue === 'TRUE' || rawValue === '1') return 'SI';
      return 'Sin solicitud';
    };

    const exportGeneratedAt = new Date();
    const reportDateFormula = `DATE(${exportGeneratedAt.getFullYear()},${exportGeneratedAt.getMonth() + 1},${exportGeneratedAt.getDate()})`;

    const applyAdminAgingFormulas = (row, cols) => {
      const solicitudIdx = cols.indexOf('EXPORT_SOLICITUD');
      const solicitudFechaIdx = cols.indexOf('EXPORT_SOLICITUD_FECHA');
      const pagadoIdx = cols.indexOf('EXPORT_PAGADO');
      const diasIdx = cols.indexOf('EXPORT_DIAS_SIN_PAGO');
      const mesesIdx = cols.indexOf('EXPORT_MESES_SIN_PAGO');

      if ([solicitudIdx, solicitudFechaIdx, pagadoIdx].some((idx) => idx < 0)) return;

      const solicitudRef = `${getExcelColLetter(solicitudIdx + 1)}${row.number}`;
      const fechaSolicitudRef = `${getExcelColLetter(solicitudFechaIdx + 1)}${row.number}`;
      const pagadoRef = `${getExcelColLetter(pagadoIdx + 1)}${row.number}`;
      const condition = `AND(${solicitudRef}="SI",${pagadoRef}="NO",${fechaSolicitudRef}<>"")`;

      const styleFormulaCell = (cell) => {
        cell.numFmt = '0';
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
        cell.font = { name: 'Arial', size: 8, bold: true, color: { argb: '92400E' } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FEF3C7' } };
      };

      if (diasIdx >= 0) {
        const cell = row.getCell(diasIdx + 1);
        cell.value = { formula: `IF(${condition},${reportDateFormula}-${fechaSolicitudRef},"")` };
        styleFormulaCell(cell);
      }

      if (mesesIdx >= 0) {
        const cell = row.getCell(mesesIdx + 1);
        cell.value = { formula: `IF(${condition},ROUND((${reportDateFormula}-${fechaSolicitudRef})/30,1),"")` };
        styleFormulaCell(cell);
        cell.numFmt = '0.0';
      }
    };

    // Filas
    rows.forEach((r) => {
      const rowPartidas = Array.isArray(r.partidas) ? r.partidas : [];

      if (isSummary || isPendientes) {
        const descLista = rowPartidas
          .map(p => `- ${p.DESCRIPCION_PART || p.DESCRIPCION || p.CONCEPTO_PROYECTO || 'S/D'}${p.CANTIDAD ? ` (Ct: ${p.CANTIDAD})` : ''}`)
          .join('\n');
        const vals = finalColumns.map(k => {
          if (k === 'DETALLES_RESUMEN' || k === 'DESCRIPCION') {
            if (rowPartidas.length > 0) return normalizePartidasText(descLista);
            return r?.DESCRIPCION || r?.DESCRIPCION_PART || r?.CONCEPTO_PROYECTO || r?.NOTAS || '';
          }
          if (k === 'PENDIENTE') return r?.PENDIENTE ?? r?.RESTANTE ?? '';
          if (k === 'TIENE_SOLICITUD_TXT') return formatTieneSolicitud(r);
          if (k === 'FECHA_SOLICITUD_PAGO' || k === 'FECHA_SOLICITUD') return r?.FECHA_SOLICITUD || r?.FECHA_SOLICITUD_PAGO || r?.ULTIMA_SOLICITUD || '';
          if (k === 'AUTORIZAR' || k === 'CONFIRMACION') return '☐'; // Casilla de verificación vacía
          if (k === 'ESTATUS') return mapEstatus(r?.[k]) ?? '';
          return r?.[k] ?? '';
        });
        const summaryRow = worksheet.addRow(vals);
        const descText = String(vals[finalColumns.indexOf('DESCRIPCION')] || vals[finalColumns.indexOf('DETALLES_RESUMEN')] || '');
        const lineCount = descText ? descText.split('\n').length : 1;
        summaryRow.height = Math.max(18, Math.max(lineCount, rowPartidas.length) * 14);
        applyMasterRowStyle(summaryRow, finalColumns, true);
        applyAdminAgingFormulas(summaryRow, finalColumns);

        // Agregar validación de datos (lista desplegable con casilla marcada/desmarcada)
        if (isPendientes) {
          const autIdx = finalColumns.indexOf('AUTORIZAR');
          const confIdx = finalColumns.indexOf('CONFIRMACION');
          if (autIdx !== -1) {
            const cellAut = summaryRow.getCell(autIdx + 1);
            cellAut.dataValidation = {
              type: 'list',
              allowBlank: true,
              formulae: ['"☑,☐"']
            };
          }
          if (confIdx !== -1) {
            const cellConf = summaryRow.getCell(confIdx + 1);
            cellConf.dataValidation = {
              type: 'list',
              allowBlank: true,
              formulae: ['"☑,☐"']
            };
          }
        }
        const descIdx = finalColumns.indexOf('DESCRIPCION') !== -1
          ? finalColumns.indexOf('DESCRIPCION')
          : finalColumns.indexOf('DETALLES_RESUMEN');
        if (descIdx !== -1) {
          const c = summaryRow.getCell(descIdx + 1);
          c.alignment = { wrapText: true, vertical: 'top', horizontal: 'left' };
          c.font = { name: 'Arial', size: 8, bold: true };
        }
      } else {
        const baseVals = finalColumns.map(k => {
          if (k === 'DESCRIPCION' || k === 'DETALLES_RESUMEN') {
            if (!r?.[k] || Array.isArray(r?.partidas)) {
              const rowPartidas = Array.isArray(r?.partidas) ? r.partidas : [];
              if (rowPartidas.length > 0) {
                const descLista = rowPartidas
                  .map(p => `- ${p.DESCRIPCION_PART || p.DESCRIPCION || p.CONCEPTO_PROYECTO || 'S/D'}${p.CANTIDAD ? ` (Ct: ${p.CANTIDAD})` : ''}`)
                  .join('\n');
                return normalizePartidasText(descLista);
              }
            }
            return r?.DESCRIPCION || r?.DESCRIPCION_PART || r?.CONCEPTO_PROYECTO || r?.NOTAS || '';
          }
          if (k === 'PENDIENTE') return r?.PENDIENTE ?? r?.RESTANTE ?? '';
          if (k === 'TIENE_SOLICITUD_TXT') return formatTieneSolicitud(r);
          if (k === 'FECHA_SOLICITUD_PAGO') return r?.FECHA_SOLICITUD_PAGO || r?.ULTIMA_SOLICITUD || '';
          if (k === 'ESTATUS') return mapEstatus(r?.[k]) ?? '';
          return r?.[k] ?? '';
        });
        const masterRow = worksheet.addRow(baseVals);
        const descText = String(baseVals[finalColumns.indexOf('DESCRIPCION')] || '');
        const lineCount = descText ? descText.split('\n').length : 1;
        masterRow.height = Math.max(18, lineCount * 14);
        applyMasterRowStyle(masterRow, finalColumns, false);
        applyAdminAgingFormulas(masterRow, finalColumns);

        if (isDetailed && rowPartidas.length > 0) {
          const subHeaderVals = [];
          subHeaderVals[2] = '';
          partidaKeys.forEach((k, idx) => { subHeaderVals[idx + 3] = colLabels[k] || k; });
          const subHeaderRow = worksheet.addRow(subHeaderVals);
          subHeaderRow.height = 16;
          subHeaderRow.outlineLevel = 1;
          applySubHeaderStyle(subHeaderRow, 3, 2 + partidaKeys.length);

          rowPartidas.forEach((p, pIdx) => {
            const pVals = [];
            pVals[2] = '';
            partidaKeys.forEach((pk, idx) => { pVals[idx + 3] = p?.[pk] ?? ''; });
            const pRow = worksheet.addRow(pVals);
            pRow.height = 16;
            pRow.outlineLevel = 1;
            applyPartidaRowStyle(pRow, 3, 2 + partidaKeys.length, pIdx === rowPartidas.length - 1, partidaKeys);
          });
        }
      }
    });

    function applyMasterRowStyle(row, cols, isSumMode) {
      row.eachCell((cell, colNumber) => {
        const key = cols[colNumber - 1];
        if (!key && !isSumMode) return;
        const raw = cell.value;
        cell.font = { name: 'Arial', size: 8, color: { argb: '000000' }, bold: true };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFF' } };
        cell.alignment = { vertical: 'middle', horizontal: 'left' };
        cell.border = { bottom: { style: 'thin', color: { argb: 'D1D5DB' } } };
        if (key === 'DESCRIPCION') {
          cell.alignment = { wrapText: true, vertical: 'top', horizontal: 'left' };
        }
        if (DATE_KEYS.includes(key)) {
          const d = toExcelDate(raw);
          if (d instanceof Date && !isNaN(d.getTime())) {
            cell.value = d; cell.numFmt = 'dd/mm/yyyy'; cell.alignment = { horizontal: 'center' };
          }
        }
        if ((MONEY_KEYS.includes(key) || key?.includes('TOTAL')) && isNumberLike(raw)) {
          cell.value = toNumber(raw); cell.numFmt = '$#,##0.00'; cell.alignment = { horizontal: 'right' };
          cell.font = { name: 'Arial', size: 8, bold: true };
        }
        if (key === 'ESTATUS') {
          const v = String(raw ?? '').toLowerCase();
          const fill = v.includes('pagada') ? 'DCEDC8' : v.includes('autoriz') ? 'BBDEFB' : v.includes('cancel') || v.includes('rechaz') ? 'FFEBEE' : v.includes('proceso') ? 'FFF9C4' : 'FFFFFF';
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: fill } };
          cell.alignment = { horizontal: 'center' }; cell.font = { name: 'Arial', size: 8, bold: true };
        }
        if (ADMIN_TRACKING_EXPORT_KEYS.has(key) && !key.endsWith('_FECHA')) {
          const v = String(raw ?? '').trim().toUpperCase();
          const isYes = v === 'SI' || v === 'SÍ';
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: isYes ? 'E8F5E9' : 'FFEBEE' },
          };
          cell.font = { name: 'Arial', size: 8, bold: true, color: { argb: isYes ? '087F23' : 'B91C1C' } };
          cell.alignment = { horizontal: 'center', vertical: 'middle' };
        }
        if (key === 'AUTORIZAR' || key === 'CONFIRMACION') {
          cell.alignment = { horizontal: 'center', vertical: 'middle' };
          cell.font = { name: 'Arial', size: 12, bold: true };
        }
      });
    }

    function applySubHeaderStyle(row, startCol, endCol) {
      for (let i = startCol; i <= endCol; i++) {
        const cell = row.getCell(i);
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'F8FAFC' } };
        cell.font = { name: 'Arial', size: 8, color: { argb: '475569' }, bold: true };
        cell.alignment = { horizontal: 'center' };
        cell.border = { bottom: { style: 'thin', color: { argb: 'CBD5E1' } } };
      }
    }

    function applyPartidaRowStyle(row, startCol, endCol, isLast, keys) {
      for (let i = startCol; i <= endCol; i++) {
        const cell = row.getCell(i);
        const key = keys[i - startCol];
        const raw = cell.value;
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFF' } };
        cell.font = { name: 'Arial', size: 8, color: { argb: '000000' } };
        cell.alignment = { horizontal: 'left' };
        cell.border = {
          bottom: { style: isLast ? 'thin' : 'hair', color: { argb: 'E2E8F0' } },
          left: { style: i === startCol ? 'medium' : 'thin', color: { argb: 'CBD5E1' } },
        };
        if (i === startCol) cell.border.left = { style: 'medium', color: { argb: '3B82F6' } };
        if (isNumberLike(raw) && (key?.includes('TOTAL') || key?.includes('COSTO') || key?.includes('CANTIDAD') || key === 'COSTOU' || key === 'TOTAL_PART')) {
          cell.value = toNumber(raw);
          if (!key.includes('CANTIDAD')) cell.numFmt = '$#,##0.00';
          cell.alignment = { horizontal: 'right' };
        }
      }
    }

    // Anchos de columna
    finalColumns.forEach((k, i) => {
      if (k === 'DETALLES_RESUMEN' || k === 'DESCRIPCION') { worksheet.getColumn(i + 1).width = 60; return; }
      const label = finalLabels[i] || k;
      let max = label.length;
      rows.forEach(r => {
        const v = r?.[k];
        const s = Array.isArray(v) ? v.join(', ') : String(v ?? '');
        if (s.length > max) max = s.length;
      });
      worksheet.getColumn(i + 1).width = Math.min(Math.max(max + 3, 12), 55);
    });

    worksheet.views = [{ state: 'frozen', ySplit: 1 }];
    const lastLetter = getExcelColLetter(finalColumns.length);
    worksheet.autoFilter = `A1:${lastLetter}1`;

    await downloadWorkbook(workbook, outFileName);
  };

  const downloadWorkbook = async (workbook, outFileName) => {
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = outFileName;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  return (
    <Box>
      {(useLegacyMode || onFetchAll) ? (
        <Tooltip title={loading ? 'Descargando…' : 'Exportar a Excel'}>
          <span>
            <IconButton
              size="small"
              disabled={isButtonDisabled}
              onClick={() => openDateDialog(legacyExportOptions[0]?.key || 'summary')}
              sx={{
                width: 22,
                height: 22,
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                bgcolor: loading ? '#f0fdf4' : '#ffffff',
                color: '#16a34a',
                boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                transition: 'all 0.2s ease',
                '&:hover': {
                  bgcolor: '#f0fdf4',
                  borderColor: '#16a34a',
                  color: '#15803d',
                  transform: 'translateY(2px) rotate(-12deg)',
                  boxShadow: '0 2px 6px rgba(22, 163, 74, 0.15)',
                },
                '&.Mui-disabled': {
                  bgcolor: '#f8fafc',
                  borderColor: '#e2e8f0',
                  color: '#cbd5e1',
                },
                ...btnSx,
              }}
            >
              {loading ? (
                <CircularProgress size={11} sx={{ color: '#16a34a' }} />
              ) : (
                <SimCardDownloadIcon sx={{ fontSize: 13 }} />
              )}
            </IconButton>
          </span>
        </Tooltip>
      ) : (
        <Tooltip title={loading ? 'Descargando…' : 'Exportar a Excel'}>
          <span>
            <IconButton
              size="small"
              disabled={isButtonDisabled}
              onClick={() => handleExport('generic')}
              sx={{
                width: 22,
                height: 22,
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                bgcolor: loading ? '#f0fdf4' : '#ffffff',
                color: '#16a34a',
                boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                transition: 'all 0.2s ease',
                '&:hover': {
                  bgcolor: '#f0fdf4',
                  borderColor: '#16a34a',
                  color: '#15803d',
                  transform: 'translateY(2px) rotate(-12deg)',
                  boxShadow: '0 2px 6px rgba(22, 163, 74, 0.15)',
                },
                '&.Mui-disabled': {
                  bgcolor: '#f8fafc',
                  borderColor: '#e2e8f0',
                  color: '#cbd5e1',
                },
              }}
            >
              {loading ? (
                <CircularProgress size={11} sx={{ color: '#16a34a' }} />
              ) : (
                <SimCardDownloadIcon sx={{ fontSize: 13 }} />
              )}
            </IconButton>
          </span>
        </Tooltip>
      )}

      {/* Diálogo de rango de fechas */}
      <Dialog
        open={dateDialog.open}
        disableRestoreFocus
        onClose={closeDateDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" alignItems="center" gap={1}>
            <DateRangeIcon sx={{ color: '#107C41', fontSize: 22 }} />
            <Typography fontWeight={700} fontSize="0.95rem" color="#1e293b">
              Exportar reporte
            </Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
            Selecciona el formato y, si lo necesitas, limita el resultado por Fecha OC.
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ pt: 1.25 }}>
          <Stack spacing={1.5}>
            <Box>
              <Typography fontSize="0.72rem" fontWeight={800} color="#64748b" textTransform="uppercase" mb={0.75}>
                Tipo de reporte
              </Typography>
              <Stack spacing={0.75}>
                {legacyExportOptions.map(option => {
                  const currentVariant = dateDialog.variant || 'summary';
                  const selected = option.key === currentVariant;
                  return (
                    <Box
                      key={option.key}
                      role="button"
                      tabIndex={0}
                      onClick={() => {
                        setDateDialog(prev => ({ ...prev, variant: option.key }));
                        if (option.key === 'pendientes') {
                          setExportScope('dateRange');
                        }
                      }}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          setDateDialog(prev => ({ ...prev, variant: option.key }));
                          if (option.key === 'pendientes') {
                            setExportScope('dateRange');
                          }
                        }
                      }}
                      sx={{
                        border: `1px solid ${selected ? '#107C41' : '#dbe3ef'}`,
                        backgroundColor: selected ? '#f0fdf4' : '#ffffff',
                        borderRadius: '10px',
                        px: 1.25,
                        py: 1,
                        cursor: 'pointer',
                        transition: 'all 0.15s ease',
                        '&:hover': {
                          borderColor: selected ? '#107C41' : '#94a3b8',
                          backgroundColor: selected ? '#ecfdf3' : '#f8fafc',
                        },
                      }}
                    >
                      <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
                        <Box>
                          <Typography fontSize="0.82rem" fontWeight={800} color="#0f172a">
                            {option.label}
                          </Typography>
                          <Typography fontSize="0.7rem" color="#64748b" mt={0.25}>
                            {option.description}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            width: 16,
                            height: 16,
                            borderRadius: '50%',
                            border: `2px solid ${selected ? '#107C41' : '#cbd5e1'}`,
                            backgroundColor: selected ? '#107C41' : '#fff',
                            boxShadow: selected ? 'inset 0 0 0 3px #fff' : 'none',
                            flex: '0 0 auto',
                          }}
                        />
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>
            </Box>

            <Box
              sx={{
                border: '1px solid #e2e8f0',
                borderRadius: '10px',
                p: 1.25,
                backgroundColor: '#f8fafc',
              }}
            >
              <Typography fontSize="0.72rem" fontWeight={800} color="#64748b" textTransform="uppercase" mb={1}>
                Alcance del reporte
              </Typography>
              <Stack spacing={0.75}>
                {exportScopeOptions
                  .filter(option => dateDialog.variant !== 'pendientes' || option.key === 'dateRange')
                  .map(option => {
                  const selected = option.key === exportScope;
                  return (
                    <Box
                      key={option.key}
                      role="button"
                      tabIndex={0}
                      onClick={() => {
                        setExportScope(option.key);
                        if (option.key === 'all') {
                          setDateFrom('');
                          setDateTo('');
                        }
                      }}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          setExportScope(option.key);
                          if (option.key === 'all') {
                            setDateFrom('');
                            setDateTo('');
                          }
                        }
                      }}
                      sx={{
                        border: `1px solid ${selected ? '#2563eb' : '#dbe3ef'}`,
                        backgroundColor: selected ? '#eff6ff' : '#ffffff',
                        borderRadius: '9px',
                        px: 1,
                        py: 0.85,
                        cursor: 'pointer',
                        transition: 'all 0.15s ease',
                        '&:hover': {
                          borderColor: selected ? '#2563eb' : '#94a3b8',
                          backgroundColor: selected ? '#eff6ff' : '#ffffff',
                        },
                      }}
                    >
                      <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
                        <Box>
                          <Typography fontSize="0.78rem" fontWeight={800} color="#0f172a">
                            {option.label}
                          </Typography>
                          <Typography fontSize="0.68rem" color="#64748b" mt={0.15}>
                            {option.description}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            width: 14,
                            height: 14,
                            borderRadius: '50%',
                            border: `2px solid ${selected ? '#2563eb' : '#cbd5e1'}`,
                            backgroundColor: selected ? '#2563eb' : '#fff',
                            boxShadow: selected ? 'inset 0 0 0 3px #fff' : 'none',
                            flex: '0 0 auto',
                          }}
                        />
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} mt={1.25}>
                <TextField
                  label="Fecha inicio"
                  type="date"
                  size="small"
                  fullWidth
                  disabled={exportScope === 'all'}
                  value={dateFrom}
                  onChange={e => setDateFrom(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ max: dateTo || undefined }}
                />
                <TextField
                  label="Fecha fin"
                  type="date"
                  size="small"
                  fullWidth
                  disabled={exportScope === 'all'}
                  value={dateTo}
                  onChange={e => setDateTo(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: dateFrom || undefined }}
                />
              </Stack>
              <Typography fontSize="0.68rem" color="#64748b" mt={1}>
                {exportScope === 'filters' && 'Puedes dejar el rango vacio para usar solo los filtros actuales.'}
                {exportScope === 'dateRange' && 'Para este alcance el rango de fechas es obligatorio.'}
                {exportScope === 'all' && 'Las fechas se ignoran para descargar todo sin filtros.'}
              </Typography>
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between', gap: 1 }}>
          <Button
            onClick={closeDateDialog}
            size="small"
            sx={{ textTransform: 'none', color: '#64748b', borderRadius: '8px' }}
          >
            Cancelar
          </Button>

          <Button
            onClick={handleExportWithDates}
            variant="contained"
            size="small"
            disabled={Boolean(
              (exportScope === 'dateRange' && (!dateFrom || !dateTo)) ||
              (exportScope !== 'all' && ((dateFrom && !dateTo) || (!dateFrom && dateTo)))
            )}
            startIcon={<SimCardDownloadIcon sx={{ fontSize: 16 }} />}
            sx={{
              textTransform: 'none',
              borderRadius: '8px',
              backgroundColor: '#107C41',
              fontWeight: 700,
              minWidth: 170,
              '&:hover': { backgroundColor: '#0d6334' },
              '&.Mui-disabled': {
                backgroundColor: '#9ca3af',
                color: '#f3f4f6'
              }
            }}
          >
            {exportScope === 'all'
              ? `Descargar ${selectedExportOption?.label || 'reporte'} completo`
              : exportScope === 'dateRange'
                ? `Descargar ${selectedExportOption?.label || 'reporte'} por rango`
                : `Descargar ${selectedExportOption?.label || 'reporte'} filtrado`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ExportButton;
